import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';

import { ReactComponent as AngleUpIcon } from 'assets/angle-up.svg';
import remixIcon from 'assets/remix-icon.svg';
import remixSpinner from 'assets/remix-spinner.gif';

import { setDesignReviewHistory } from 'Redux/Actions/designReviewActions';
import { setCurrentRecord } from 'Redux/Actions/historyActions';

import { trackEvent } from 'services/eventTracking';
import { getDesignReviewPostProcess, triggerDesignRemix } from 'services/teamDashboardReviews';

import { DESIGN_REMIX_POLL_INTERVAL, DESIGN_REMIX_STATUS } from 'utlis/constants';

const formatDeeperInsightForRemix = insight => {
  if (!insight || typeof insight !== 'object') {
    return '';
  }

  const {
    detailed_feedback: detailedFeedback,
    high_impact_recommendation: highImpactRecommendation,
    action_steps: actionSteps,
    most_impacted_segment: mostImpactedSegment,
    primary_metrics_moved: primaryMetricsMoved,
    reason,
  } = insight;

  const lines = [];

  if (detailedFeedback || reason) {
    lines.push(detailedFeedback || reason);
  }

  const hasHighImpactSection = highImpactRecommendation
    || (Array.isArray(actionSteps) && actionSteps.length > 0);

  if (hasHighImpactSection) {
    if (lines.length) {
      lines.push('');
    }
    lines.push('High-impact recommendation');

    if (highImpactRecommendation) {
      lines.push(highImpactRecommendation);
    }
    if (Array.isArray(actionSteps) && actionSteps.length > 0) {
      actionSteps.forEach((step, index) => {
        lines.push(`${index + 1}. ${step}`);
      });
    }
  }

  if (mostImpactedSegment) {
    if (lines.length) {
      lines.push('');
    }
    lines.push('Most impacted segment');
    lines.push(mostImpactedSegment);
  }

  if (Array.isArray(primaryMetricsMoved) && primaryMetricsMoved.length > 0) {
    if (lines.length) {
      lines.push('');
    }
    lines.push('Primary metrics moved');
    lines.push(primaryMetricsMoved.join(', '));
  }

  if (reason) {
    if (lines.length) {
      lines.push('');
    }
    lines.push('Why');
    lines.push(reason);
  }

  return lines.join('\n');
};

export const DesignInspirationPanel = ({
  insight,
  insightUuid,
  currentFeedbackUuid,
  isPublicView = false,
}) => {
  const dispatch = useDispatch();

  const currentRecord = useSelector(state => state.historyReducer.currentRecord);
  const designReviewHistory = useSelector(state => state.designReview.designReviewHistory);
  const userData = useSelector(state => state.userStore);

  const [remixStatus, setRemixStatus] = useState(null);
  const [remixProcessUuid, setRemixProcessUuid] = useState(null);
  const [remixPreview, setRemixPreview] = useState(null);
  const [isRemixExpanded, setIsRemixExpanded] = useState(false);
  const [remixError, setRemixError] = useState('');
  const [isTriggeringRemix, setIsTriggeringRemix] = useState(false);
  const [designImageUrlState, setDesignImageUrlState] = useState(null);
  const [isFetchingImage, setIsFetchingImage] = useState(false);
  const [imageError, setImageError] = useState('');

  const isDeeperInsightFeedback = useMemo(
    () => Boolean(insight?.isDeeperInsight || insight?.category === 'deeper-insights'),
    [insight]
  );

  const previewSrc = useMemo(() => remixPreview?.upload_info?.url || null, [remixPreview]);
  const previewHtmlUrl = useMemo(
    () => remixPreview?.html_upload_info?.url || null,
    [remixPreview]
  );

  const hasImagePreview = Boolean(previewSrc);
  const isRemixIdle = remixStatus === null || remixStatus === undefined;
  const isRemixInProgress = remixStatus === DESIGN_REMIX_STATUS.IN_PROGRESS;
  const isRemixComplete = remixStatus === DESIGN_REMIX_STATUS.COMPLETED;

  const ensureDesignImageUrl = useCallback(async () => {
    if (designImageUrlState) {
      return designImageUrlState;
    }

    const framePath = currentRecord?.extra_data?.frame_images?.[0]
      || currentRecord?.extra_data?.frame_image;

    setIsFetchingImage(true);
    setImageError('');

    try {
      if (!framePath) {
        throw new Error('Missing design image path');
      }

      const prefixUrl = process.env.REACT_APP_FIREBASE_STORAGE_PREFIX;
      const postfixUrl = '?alt=media';
      const imageUrl = `${prefixUrl}${encodeURIComponent(framePath)}${postfixUrl}`;

      if (!imageUrl) {
        throw new Error('Design image unavailable');
      }

      setDesignImageUrlState(imageUrl);

      return imageUrl;
    }
    catch (error) {
      trackEvent('[error] design inspiration image download failed', {
        insightUuid,
        recordUuid: currentRecord?.uuid,
        feedbackUuid: currentFeedbackUuid,
        message: error?.message,
      });

      setImageError('Unable to fetch design image URL');

      return null;
    }
    finally {
      setIsFetchingImage(false);
    }
  }, [currentRecord, designImageUrlState, insightUuid, currentFeedbackUuid]);

  const updateRecordRemix = useCallback((payload = {}) => {
    if (!currentRecord || !insightUuid) {
      return;
    }

    const extraData = currentRecord.extra_data || {};
    const postProcesses = extraData.post_processes || {};
    const appliedPreview = postProcesses.applied_feedback_preview || {};
    const deeperInsights = postProcesses.deeper_insights || {};

    const existingAppliedEntry = appliedPreview[insightUuid] || {};
    const payloadRemixPreview = payload.remix_preview || {};

    const mergedAppliedEntry = {
      ...existingAppliedEntry,
      ...payload,
      remix_preview: {
        ...(existingAppliedEntry.remix_preview || {}),
        ...payloadRemixPreview,
        status: payloadRemixPreview.status
          ?? payload.status
          ?? existingAppliedEntry.remix_preview?.status,
        process_uuid: payloadRemixPreview.process_uuid
          ?? payload.process_uuid
          ?? existingAppliedEntry.remix_preview?.process_uuid,
        error_message: payloadRemixPreview.error_message
          ?? payload.error_message
          ?? existingAppliedEntry.remix_preview?.error_message,
        upload_info: payloadRemixPreview.upload_info
          ?? payload.upload_info
          ?? existingAppliedEntry.remix_preview?.upload_info,
        html_upload_info: payloadRemixPreview.html_upload_info
          ?? payload.html_upload_info
          ?? existingAppliedEntry.remix_preview?.html_upload_info,
        generated_at: payloadRemixPreview.generated_at
          ?? payload.generated_at
          ?? existingAppliedEntry.remix_preview?.generated_at,
      },
    };

    const updatedAppliedPreview = {
      ...appliedPreview,
      [insightUuid]: mergedAppliedEntry,
    };

    let updatedDeeperInsightsFeedback = deeperInsights.feedback;

    if (Array.isArray(deeperInsights.feedback)) {
      updatedDeeperInsightsFeedback = deeperInsights.feedback.map(insightItem => {
        if (String(insightItem?.uuid) !== String(insightUuid)) {
          return insightItem;
        }

        const existingRemixPreview = insightItem.remix_preview || {};
        const mergedRemixPreview = {
          ...existingRemixPreview,
          ...payloadRemixPreview,
        };

        const resolvedStatus = payloadRemixPreview.status
          ?? payload.status
          ?? existingRemixPreview.status;

        const resolvedProcessUuid = payloadRemixPreview.process_uuid
          ?? payload.process_uuid
          ?? existingRemixPreview.process_uuid;

        const resolvedPreviewBase64 = payloadRemixPreview.upload_info
          ?? payload.upload_info
          ?? existingRemixPreview.upload_info;

        const resolvedHtmlUpload = payloadRemixPreview.html_upload_info
          ?? payload.html_upload_info
          ?? existingRemixPreview.html_upload_info;

        const resolvedGeneratedAt = payloadRemixPreview.generated_at
          ?? payload.generated_at
          ?? existingRemixPreview.generated_at;

        const resolvedErrorMessage = payloadRemixPreview.error_message
          ?? payload.error_message
          ?? existingRemixPreview.error_message;

        return {
          ...insightItem,
          remix_preview: {
            ...mergedRemixPreview,
            status: resolvedStatus,
            process_uuid: resolvedProcessUuid,
            upload_info: resolvedPreviewBase64,
            html_upload_info: resolvedHtmlUpload,
            generated_at: resolvedGeneratedAt,
            error_message: resolvedErrorMessage,
          },
          upload_info: resolvedPreviewBase64 ?? insightItem.upload_info,
          html_upload_info: resolvedHtmlUpload ?? insightItem.html_upload_info,
        };
      });
    }

    const updatedRecord = {
      ...currentRecord,
      extra_data: {
        ...extraData,
        post_processes: {
          ...postProcesses,
          applied_feedback_preview: updatedAppliedPreview,
          deeper_insights: {
            ...deeperInsights,
            feedback: updatedDeeperInsightsFeedback,
          },
        },
      },
    };

    dispatch(setCurrentRecord(updatedRecord));

    if (Array.isArray(designReviewHistory?.results)) {
      const historyIndex = designReviewHistory.results.findIndex(
        record => String(record?.uuid) === String(currentRecord?.uuid)
      );

      if (historyIndex > -1) {
        const updatedHistory = {
          ...designReviewHistory,
          results: [...designReviewHistory.results],
        };

        updatedHistory.results[historyIndex] = {
          ...updatedHistory.results[historyIndex],
          ...updatedRecord,
        };

        dispatch(setDesignReviewHistory(updatedHistory));
      }
    }
  }, [currentRecord, dispatch, insightUuid, designReviewHistory]);

  useEffect(() => {
    if (!isDeeperInsightFeedback || !insightUuid) {
      setRemixStatus(null);
      setRemixProcessUuid(null);
      setRemixPreview(null);
      setRemixError('');
      setIsRemixExpanded(false);

      return;
    }

    const previewBucket = currentRecord?.extra_data?.post_processes?.applied_feedback_preview || {};
    const existingEntry = previewBucket ? previewBucket[insightUuid] : null;

    let insightPreview = null;
    const deeperInsightsList = currentRecord?.extra_data?.post_processes?.deeper_insights?.feedback || [];

    if (Array.isArray(deeperInsightsList)) {
      const insightMatch = deeperInsightsList.find(item => String(item?.uuid) === String(insightUuid));

      if (insightMatch && insightMatch.remix_preview) {
        insightPreview = insightMatch.remix_preview;
      }
    }

    const finalPreview = insightPreview || existingEntry;

    if (finalPreview) {
      setRemixStatus(finalPreview.status ?? DESIGN_REMIX_STATUS.COMPLETED);
      setRemixProcessUuid(finalPreview.process_uuid || null);
      setRemixPreview(finalPreview);
      setRemixError(finalPreview.error_message || '');

      if (
        finalPreview.status === DESIGN_REMIX_STATUS.COMPLETED
        && (finalPreview.upload_info || finalPreview.html_upload_info)
      ) {
        setIsRemixExpanded(prev => prev);
      }
    }
    else {
      setRemixStatus(null);
      setRemixProcessUuid(null);
      setRemixPreview(null);
      setRemixError('');
      setIsRemixExpanded(false);
    }
  }, [currentRecord, insightUuid, isDeeperInsightFeedback]);

  useEffect(() => {
    if (
      !isDeeperInsightFeedback
      || !remixProcessUuid
      || remixStatus !== DESIGN_REMIX_STATUS.IN_PROGRESS
      || isPublicView
    ) {
      return undefined;
    }

    let cancelled = false;
    let intervalId = null;

    const stopPolling = () => {
      cancelled = true;

      if (intervalId) {
        clearInterval(intervalId);
      }
    };

    const checkStatus = async () => {
      try {
        const postProcess = await getDesignReviewPostProcess(remixProcessUuid);

        if (!postProcess || cancelled) {
          return;
        }

        if (postProcess.status !== undefined) {
          setRemixStatus(postProcess.status);
        }

        if (postProcess.status === DESIGN_REMIX_STATUS.COMPLETED) {
          const resultData = postProcess.result_data || {};

          setRemixPreview(resultData);
          setRemixError('');
          setIsRemixExpanded(true);

          updateRecordRemix({
            status: DESIGN_REMIX_STATUS.COMPLETED,
            process_uuid: postProcess.uuid,
            upload_info: resultData.upload_info,
            html_upload_info: resultData.html_upload_info,
            generated_at: resultData.generated_at,
            remix_preview: {
              status: DESIGN_REMIX_STATUS.COMPLETED,
              upload_info: resultData.upload_info,
              html_upload_info: resultData.html_upload_info,
              generated_at: resultData.generated_at,
              process_uuid: postProcess.uuid,
            },
          });

          stopPolling();
        }
        else if (postProcess.status === DESIGN_REMIX_STATUS.FAILED) {
          const errorMessage = postProcess.error_message || 'Design remix failed';

          setRemixError(errorMessage);

          updateRecordRemix({
            status: DESIGN_REMIX_STATUS.FAILED,
            process_uuid: postProcess.uuid,
            error_message: errorMessage,
            remix_preview: {
              status: DESIGN_REMIX_STATUS.FAILED,
              error_message: errorMessage,
              process_uuid: postProcess.uuid,
            },
          });

          stopPolling();
        }
      }
      catch (error) {
        if (!cancelled) {
          setRemixError(error?.response?.data?.message || 'Unable to fetch remix status');
        }
      }
    };

    checkStatus();

    intervalId = setInterval(checkStatus, DESIGN_REMIX_POLL_INTERVAL);

    return () => {
      stopPolling();
    };
  }, [isDeeperInsightFeedback, remixProcessUuid, remixStatus, updateRecordRemix, isPublicView]);

  const handleGenerateRemix = useCallback(async () => {
    if (!isDeeperInsightFeedback || !insightUuid || isTriggeringRemix) {
      return;
    }

    setIsTriggeringRemix(true);
    setRemixError('');
    setImageError('');

    try {
      const imageUrlForRemix = await ensureDesignImageUrl();

      if (!imageUrlForRemix) {
        throw new Error('Design image unavailable');
      }

      const response = await triggerDesignRemix({
        insightUuid,
        imageUrl: imageUrlForRemix,
        deeperInsightText: formatDeeperInsightForRemix(insight ?? {}),
        figmaUserId: userData?.figma_user_id || userData?.figmaUserId,
        designReviewUuid: currentFeedbackUuid,
      });

      setRemixProcessUuid(response.process_uuid);
      setRemixStatus(response.status ?? DESIGN_REMIX_STATUS.IN_PROGRESS);
      setRemixPreview(null);
      setIsRemixExpanded(false);

      updateRecordRemix({
        status: response.status ?? DESIGN_REMIX_STATUS.IN_PROGRESS,
        process_uuid: response.process_uuid,
        upload_info: null,
        html_upload_info: null,
        generated_at: null,
        remix_preview: {
          status: DESIGN_REMIX_STATUS.IN_PROGRESS,
          process_uuid: response.process_uuid,
          html_upload_info: null,
        },
      });
    }
    catch (error) {
      const remixErrorMsg = error?.response?.data?.message
        || error?.message || 'Failed to start design remix. Please try again.';

      setRemixError(remixErrorMsg);
    }
    finally {
      setIsTriggeringRemix(false);
    }
  }, [
    currentFeedbackUuid,
    ensureDesignImageUrl,
    insight,
    insightUuid,
    isDeeperInsightFeedback,
    isTriggeringRemix,
    updateRecordRemix,
    userData,
  ]);

  const handleOpenHtmlPreview = useCallback(() => {
    if (!previewHtmlUrl) {
      return;
    }

    const baseURL = `${process.env.REACT_APP_DESIGN_PRO_BASE_URL || window.location.origin}preview-share/`;
    const finalURL = baseURL + remixProcessUuid;

    window.open(finalURL, '_blank', 'noopener,noreferrer');
  }, [previewHtmlUrl, remixProcessUuid]);

  if (!isDeeperInsightFeedback) {
    return null;
  }

  // In public view, only show the panel if there's a completed preview
  // Don't show idle state or in-progress state to public users
  if (isPublicView && (isRemixIdle || isRemixInProgress) && !hasImagePreview && !previewHtmlUrl) {
    return null;
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex justify-between items-center">
        <p className="text-[12px] leading-4 text-black/50 tracking-[0.055px]">
          Inspiration
        </p>
        {isRemixComplete && !hasImagePreview && previewHtmlUrl && (
          <div className="flex gap-2">
            <button
              type="button"
              className="bg-transparent border border-black/20 text-gray-900/90 text-[11px]
                font-medium leading-4 tracking-[0.055px] rounded-md px-3 py-2 cursor-pointer
                hover:bg-black/[0.04]"
              onClick={handleOpenHtmlPreview}
            >
              Open prototype
            </button>
          </div>
        )}
      </div>

      {isRemixIdle && !isPublicView && (
        <button
          className="flex flex-row justify-center items-center gap-8 py-2.5 px-5 bg-white border
            border-[#E1E4E8] rounded-lg shadow-[0px_2px_4px_-1px_rgba(0,0,0,0.04),0px_1px_1px_rgba(0,0,0,0.02)]
            disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          onClick={handleGenerateRemix}
          disabled={!insightUuid || isTriggeringRemix || isFetchingImage}
        >
          <img className="w-4 h-4" src={remixIcon} alt="Remix icon" />
          <span className="text-[14px] font-medium leading-4 tracking-[0.055px]">
            {isTriggeringRemix || isFetchingImage ? 'Preparing…' : 'Generate design inspiration'}
          </span>
        </button>
      )}

      {isRemixInProgress && !isPublicView && (
        <div className="flex justify-center items-center rounded-xl border border-[#E6E6E6] bg-white select-none py-60">
          <div className="flex flex-col justify-center items-center gap-24">
            <img src={remixSpinner} alt="Remix in progress" className="w-[34px] h-[34px]" />
            <div className="font-normal text-[12px] leading-4 text-center text-black/50">
              Generating inspiration in up to 5 minutes
            </div>
          </div>
        </div>
      )}

      {isRemixComplete && hasImagePreview && (
        <div className="relative mt-1">
          <div
            className={cn(
              'relative w-full rounded-xl overflow-hidden border border-black/16 bg-white transition-all duration-300',
              {
                'max-h-[560px]': isRemixExpanded,
                'max-h-[220px]': !isRemixExpanded,
              }
            )}
          >
            <img
              src={previewSrc}
              alt="Design remix preview"
              className="w-full block object-cover"
            />
          </div>
          <div
            className="absolute left-3 right-3 bottom-3 flex items-center gap-8 px-2 py-1.5 rounded-lg
              bg-white/96 shadow-[0px_12px_24px_rgba(0,0,0,0.12)]"
          >
            <button
              type="button"
              className="flex items-center justify-center p-2 rounded-md
                border border-black/20 bg-white text-black/90 cursor-pointer"
              onClick={() => setIsRemixExpanded(prev => !prev)}
              aria-expanded={isRemixExpanded}
              aria-label={isRemixExpanded ? 'Collapse preview' : 'Expand preview'}
            >
              <div className="flex justify-center items-center w-4 h-4">
                <AngleUpIcon
                  className={cn('transition-transform duration-200', {
                    'rotate-180': !isRemixExpanded,
                  })}
                />
              </div>
            </button>
            <button
              type="button"
              className="flex items-center px-2 py-2 rounded-md border border-black/20 bg-white
                text-black/90 text-[10px] font-medium leading-4 tracking-[0.055px] cursor-pointer
                disabled:opacity-45 disabled:cursor-not-allowed"
              onClick={handleOpenHtmlPreview}
              disabled={!previewHtmlUrl}
            >
              Open prototype
            </button>
          </div>
        </div>
      )}

      {remixStatus === DESIGN_REMIX_STATUS.FAILED && !isPublicView && (
        <>
          <p className="m-0 text-xs text-[#DC3412]">
            {remixError || 'Remix failed. Please try again.'}
          </p>
          <button
            className="flex justify-between items-center gap-2 px-2 py-2 rounded-md border border-black/20
              bg-white text-gray-800 hover:shadow-[0px_4px_8px_rgba(96,97,112,0.16),0px_0px_2px_rgba(40,41,61,0.04)]
              transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleGenerateRemix}
            disabled={isTriggeringRemix || isFetchingImage}
          >
            Try again
          </button>
        </>
      )}

      {remixError && remixStatus !== DESIGN_REMIX_STATUS.FAILED && !isRemixInProgress && (
        <p className="m-0 text-xs text-[#DC3412]">
          {remixError}
        </p>
      )}

      {imageError && (
        <p className="m-0 text-xs text-[#DC3412]">
          {imageError}
        </p>
      )}

      {!insightUuid && (
        <p className="m-0 text-[11px] text-gray-900/55">
          Remix unavailable for this insight.
        </p>
      )}
    </div>
  );
};

DesignInspirationPanel.propTypes = {
  insight: PropTypes.object.isRequired,
  insightUuid: PropTypes.string,
  currentFeedbackUuid: PropTypes.string,
  isPublicView: PropTypes.bool,
};

DesignInspirationPanel.defaultProps = {
  insightUuid: undefined,
  currentFeedbackUuid: undefined,
  isPublicView: false,
};

export default DesignInspirationPanel;

